import { useQueryClient } from '@tanstack/react-query';
import jwtDecode from 'jwt-decode';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ImpersonationContext } from 'providers/ImpersonationContext';
import { hydrateReduxSessionState } from 'services/auth/hydrateReduxSessionState';
import { useAppDispatch } from 'store/store';
import {
  clearImpersonation,
  getImpersonateId,
  impersonate,
  signIn,
  UserAttributes,
} from 'store/user';
import {
  getImpersonationCookie,
  setImpersonationCookie,
} from 'utils/impersonation';
import { getJWTToken, setJWTToken } from 'utils/jwt';
import { SplashScreen } from 'views/Auth';

interface ImpersonationProviderProps {
  children: ReactNode;
}

/**
 * ImpersonationProvider handles all impersonation initialization scenarios:
 *
 * 1. NEW IMPERSONATION (URL params present):
 *    - Practitioner clicks impersonation link from practitioner portal
 *    - URL contains: token, impersonate_id
 *    - Sets up JWT, signs in, sets cookies, navigates to results
 *
 * 2. PAGE REFRESH (cookies present, no URL params):
 *    - User refreshes page during active impersonation session
 *    - Restores Redux state from cookies
 *    - Auth is handled by regular JWT in localStorage
 *
 * 3. NORMAL FLOW (no URL params, no cookies):
 *    - Regular user session, no impersonation
 *    - Auth is handled by reading JWT from localStorage
 */
const ImpersonationProvider = ({ children }: ImpersonationProviderProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [isProcessing, setIsProcessing] = useState(false);
  const [hasProcessedUrlParams, setHasProcessedUrlParams] = useState(false);
  const [hasInitializedFromCookies, setHasInitializedFromCookies] =
    useState(false);
  const impersonateId = useSelector(getImpersonateId);
  const isResolved = hasProcessedUrlParams || hasInitializedFromCookies;
  const status = useMemo(
    () => ({ isImpersonating: isResolved ? !!impersonateId : null }),
    [isResolved, impersonateId],
  );

  // Check URL params for new impersonation
  const jwtToken = searchParams.get('token');
  const impersonateMainAccountId = searchParams.get('impersonate_id');
  const selectedKitForImpersonation = searchParams.get('kit_id');
  const fromPartnerRecs = searchParams.get('from_partner_recs');
  const partnerId = searchParams.get('partner_id');
  const openPracSection = searchParams.get('openPracSection');
  const hasImpersonationUrlParams = !!(jwtToken && impersonateMainAccountId);

  // Effect 1: Handle new impersonation from URL params
  useEffect(() => {
    if (!hasImpersonationUrlParams || hasProcessedUrlParams) return;

    const processUrlParams = async () => {
      setIsProcessing(true);

      try {
        // Decode and validate JWT
        const decoded = jwtDecode<UserAttributes>(jwtToken!);
        if (!decoded || typeof decoded === 'string') {
          console.error(
            'ImpersonationProvider: Invalid JWT token in URL params',
          );
          setIsProcessing(false);
          return;
        }

        // Set JWT token in localStorage
        setJWTToken(jwtToken!);

        // Sign in with token data
        await dispatch(signIn(decoded));

        // Clear any cached data from previous sessions
        await queryClient.removeQueries();

        // Set impersonation state in Redux
        dispatch(impersonate(impersonateMainAccountId!));

        // Set impersonation cookie for persistence
        setImpersonationCookie(impersonateMainAccountId!, {
          scope: 'host-only',
        });

        if (fromPartnerRecs && partnerId) {
          if (partnerId) navigate(`/partner-recs/${partnerId}`);
        } else if (selectedKitForImpersonation) {
          const navUrl = openPracSection
            ? `/results/${selectedKitForImpersonation}?openPracSection=${openPracSection}`
            : `/results/${selectedKitForImpersonation}`;
          navigate(navUrl, {
            replace: true,
          });
        } else {
          const navUrl = openPracSection
            ? `/results?openPracSection=${openPracSection}`
            : '/results';
          navigate(navUrl, { replace: true });
        }

        setHasProcessedUrlParams(true);
      } catch (err) {
        console.error(
          'ImpersonationProvider: Error processing URL params:',
          err,
        );
      }

      setIsProcessing(false);
    };

    processUrlParams();
  }, [
    hasImpersonationUrlParams,
    hasProcessedUrlParams,
    jwtToken,
    impersonateMainAccountId,
    dispatch,
    queryClient,
    navigate,
  ]);

  // Effect 2: Restore impersonation state from cookies (page refresh scenario)
  useEffect(() => {
    // Skip if URL params are present (Effect 1 handles this)
    if (hasImpersonationUrlParams) return;

    // Skip if already initialized
    if (hasInitializedFromCookies) return;

    const impersonationId = getImpersonationCookie();

    if (impersonationId) {
      hydrateReduxSessionState({ dispatch, impersonateId: impersonationId });
    } else {
      // The cookie is the session's proof of impersonation; a persisted id
      // without it is stale (e.g. its tab closed before the store was saved).
      dispatch(clearImpersonation());
    }

    setHasInitializedFromCookies(true);
  }, [hasImpersonationUrlParams, hasInitializedFromCookies, dispatch]);

  // Effect 3: Initialize auth from localStorage (non-impersonation case)
  useEffect(() => {
    // Skip if URL params are present (Effect 1 handles auth)
    if (hasImpersonationUrlParams) return;

    // Skip if still processing
    if (isProcessing) return;

    try {
      const storedToken = getJWTToken();
      hydrateReduxSessionState({ dispatch, token: storedToken });
    } catch (error) {
      console.error(
        'ImpersonationProvider: Error initializing auth from localStorage:',
        error,
      );
    }
  }, [hasImpersonationUrlParams, isProcessing, dispatch]);

  // Show loading screen while processing URL params
  if (hasImpersonationUrlParams && !hasProcessedUrlParams) {
    return <SplashScreen />;
  }

  return (
    <ImpersonationContext.Provider value={status}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationProvider;
